import React, { useRef, useEffect, useCallback, useState } from 'react';
import { Section, Container, PlayPauseButton } from 'components/shared';
import styled from 'styled-components';
import breakpoints from 'utils/breakpoints';
import { getFieldValue } from '@sitecore-jss/sitecore-jss-react';
import useBreakpoint from 'utils/hooks/useBreakpoint';
import getEmbedUrl from 'utils/getEmbedUrl';
import getVideoType from 'utils/getVideoType';
import {
  videoTranscriptFileUrl,
  videoTranscriptFileExtension,
} from 'utils/videoTranscriptFileUrl';
import pageTypes from 'utils/pageTypes';
import {
  useUiValue,
  setHasCommingSonnCommunity,
  openRegistrationModal,
  updateSubNavigationBarGeneralDataContext,
} from 'stores/uiContext';
import { Helmet } from '@dr.pogodin/react-helmet';
import trackAnalyticsEvent from 'utils/trackAnalyticsEvent';
import useFavorites from 'utils/hooks/useFavorites';
import { useSitecoreContext } from 'stores/sitecoreContext';
import isEditableElement from 'utils/isEditableElement';

import loadable from '@loadable/component';
const Heading = loadable(() => import('components/components/atoms/Heading'));
const Copy = loadable(() => import('components/components/atoms/Copy'));
const Button = loadable(() => import('components/components/atoms/Button'));
const ProductHeroLarge = loadable(() =>
  import('components/containers/ProductHeroLarge'),
);

const StyledSection = styled(Section)`
  min-height: 34rem;
  height: 87vh;
  background-image: url(${(props) => props.background});
  padding-bottom: 4.5rem;
  @media ${breakpoints.md} {
    padding-bottom: 1.5rem;
    height: 84vh;
  }
`;

const StyledContainer = styled(Container)`
  min-height: 29rem;
`;

const StyledHeading = styled(Heading)`
  font-size: clamp(36px, 10vw, 56px);
  line-height: 1;
  margin-bottom: 0rem;
  @media ${breakpoints.md} {
  }
`;

const StyledCopy = styled(Copy)`
  @media ${breakpoints.md} {
    max-width: 33.75rem;
  }
`;

const Video = styled.video`
  min-height: 87vh;
  @media ${breakpoints.xs} {
    min-height: 90vh;
  }
  @media ${breakpoints.md} {
    min-height: 84vh;
  }
  top: 50%;
  left: 50%;
`;

const GradientTop = styled.div`
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.7) 0%, transparent 100%);
`;

const StyledPlayPauseButton = styled(PlayPauseButton)`
  margin-bottom: 7.5rem;
  @media ${breakpoints.md} {
    margin-bottom: 7.5rem;
  }
`;
const FavoritesButton = styled.button`
  display: inline-flex;
  border: 2px solid rgba(255, 255, 255, 0.5);
  oppacity: 50%;
  color: white;
  transition: background 0.2s, border-color 0.2s;
  &:hover {
    border: 2px solid rgba(255, 255, 255, 1);
  }
`;

const ProductHero = ({ fields }) => {
  const elongatedImage = fields.elongatedImage;
  const currentPageType = getFieldValue(fields, 'currentPageType');
  const hideherooverlay = getFieldValue(fields, 'hideherooverlay');
  const displayOption = getFieldValue(fields, 'displayOption');
  const image = getFieldValue(fields, 'image') || {};
  const video =
    (displayOption === 'Video' && getFieldValue(fields, 'video')) || {};
  const title = getFieldValue(fields, 'title');
  const subtitle = getFieldValue(fields, 'subtitle');
  const communityStatus = fields.communityStatus;
  const [paused, setPaused] = useState(true);
  const isWinOS =
    typeof window !== 'undefined' && window.navigator.platform === 'Win32';
  const btnPlayPauseClass =
    'absolute right-0 bottom-0 ' + (isWinOS ? 'mr-8' : 'mr-4');

  const breakpoint = useBreakpoint(
    typeof window !== 'undefined' && window.innerWidth,
  );
  const isMobile = ['sm', 'xs'].includes(breakpoint);

  const background =
    (isMobile ? image.srcMobile : image.srcDesktop) || image.src;

  const videoRef = useRef(null);
  const [{}, dispatch] = useUiValue();

  const showFavorites = getFieldValue(fields, 'showFavorites') || false;
  const { favoritesList, saveFavorite, deleteFavorite } = useFavorites();
  const { ContextID = {} } = useSitecoreContext() || {};
  const id = ContextID.value;
  const isFavorite = favoritesList.includes(id);
  const handleFavoriteClick = (id) => {
    if (!isFavorite) {
      saveFavorite(id);
    } else {
      deleteFavorite(id);
    }
  };

  const handleModalClick = () => {
    /*trackAnalyticsEvent({
      gtagEvent: 'StickyNavFormClick',
      mouseflowTag: 'StickyNavForm',
    });*/
    dispatch(openRegistrationModal());
  };
  const handleUserKeyPress = (event) => {
    if (isEditableElement(event.target)) {
      return;
    }

    const { keyCode } = event;
    if (keyCode === 32) {
      if (videoRef && videoRef.current) {
        if (videoRef.current.paused) {
          videoRef.current.play();
          setPaused(false);
        } else {
          videoRef.current.pause();
          setPaused(true);
        }
        event.preventDefault();
      }
    }
  };

  useEffect(() => {
    setTimeout(() => {
      if (communityStatus && communityStatus.toUpperCase() === 'COMING SOON') {
        dispatch(setHasCommingSonnCommunity(true));
      }
    });
  }, [communityStatus]);

  useEffect(() => {
    window.addEventListener('keydown', handleUserKeyPress);
    return () => {
      window.removeEventListener('keydown', handleUserKeyPress);
    };
  }, [handleUserKeyPress]);

  useEffect(() => {
    if (!isMobile && videoRef && videoRef.current) {
      setTimeout(() => {
        videoRef.current.play();
        setPaused(false);
      });
    }
    // isMobile starts true (SSR breakpoint) and resolves after mount, so without
    // it here the desktop hero video never autoplays.
  }, [videoRef, isMobile]);

  const handlePlayClick = useCallback(() => {
    if (videoRef && videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play();
        setPaused(false);
      } else {
        videoRef.current.pause();
        setPaused(true);
      }
    }
  }, [videoRef]);

  const videoType = getVideoType(video.href);

  const videoTranscriptBackgroundColor =
    getFieldValue(fields, 'videoTranscriptBackgroundColor') || 'bg-white';
  const videoTrancriptText = getFieldValue(fields, 'videoTrancriptText');
  const videoTranscriptFile =
    getFieldValue(fields, 'videoTranscriptFile') || {};
  const buttonCta = getFieldValue(fields, 'buttonCta') || {};

  useEffect(() => {
    dispatch(
      updateSubNavigationBarGeneralDataContext({
        title,
        buttonCta,
        currentPageType,
      }),
    );

    return () => {
      dispatch(
        updateSubNavigationBarGeneralDataContext({
          title: '',
          buttonCta: {},
          currentPageType: '',
        }),
      );
    };
  }, [
    title,
    buttonCta && buttonCta.href,
    buttonCta && buttonCta.target,
    buttonCta && buttonCta.text,
    currentPageType,
    dispatch,
  ]);

  if (
    !isMobile &&
    elongatedImage &&
    !(displayOption === 'Video' && video && video.href)
  ) {
    return <ProductHeroLarge fields={fields} />;
  }

  return (
    <>
      {video && video.href && videoType === 'internal' && (
        <Helmet>
          <link
            fetchPriority='high'
            rel='preload'
            as='video'
            href={video.href}
          />
        </Helmet>
      )}
      {background && (
        <Helmet>
          <link
            fetchPriority='high'
            rel='preload'
            as='image'
            href={background}
          />
        </Helmet>
      )}
      <StyledSection
        id='HeaderPlanPage'
        tabIndex={'-1'}
        background={background}
        className='lg:items-start relative bg-center bg-cover
        bg-no-repeat overflow-hidden pb-12 z-15 flex'
      >
        {!isMobile && video && videoType === 'external' && (
          <Video
            as='iframe'
            className='absolute w-full object-cover transform -translate-y-1/2 -translate-x-1/2 bg-black'
            src={
              getEmbedUrl(video.href) + '&version=3&enablejsapi=1&controls=0'
            }
          />
        )}
        {!isMobile && video && videoType === 'internal' && (
          <>
            <Video
              className='absolute w-full object-cover transform -translate-y-1/2 -translate-x-1/2'
              autoPlay
              muted
              loop
              src={video.href}
              ref={videoRef}
              playsInline
            />
          </>
        )}
        <GradientTop className='absolute top-0 bottom-0 w-full' />

        <StyledContainer className='relative w-full h-full flex flex-1 flex-col items-start justify-end pb-12 md:pb-24'>
          {!(currentPageType === pageTypes.CondoPage && hideherooverlay) && (
            <>
              {communityStatus && (
                <p className='font-trade-gothic-20 uppercase text-white text-base font-medium tracking-widest mb-5'>
                  {communityStatus}
                </p>
              )}
              <div className='flex flex-row '>
                <StyledHeading
                  color='white'
                  className='text-left leading-none'
                  large={!isMobile}
                >
                  {title}
                </StyledHeading>
                <div className='flex pb-4'>
                  {showFavorites && (
                    <FavoritesButton
                      onClick={() => handleFavoriteClick(id)}
                      aria-pressed='false'
                      aria-label='Add to favorites'
                      className='ml-4 h-10 w-10 focus-ring-light cursor-pointer items-center justify-center p-0 rounded-full'
                    >
                      <svg
                        className='w-5 h-5'
                        aria-hidden='true'
                        viewBox='0 0 24 24'
                        fill={isFavorite ? '#fff' : 'none'}
                        stroke='#fff'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      >
                        <path d='M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z'></path>
                      </svg>
                    </FavoritesButton>
                  )}
                </div>
              </div>
              <StyledCopy
                color='white'
                className='text-left text-md md:text-lg leading-relaxed mb-7'
              >
                {subtitle}
              </StyledCopy>
              {buttonCta && buttonCta.text && (
                <Button
                  variant='filled'
                  color='action-blue'
                  className='text-lg'
                  onClick={() => {
                    handleModalClick();
                  }}
                  buttonDescription='Stay updated about this community'
                >
                  {buttonCta.text}
                </Button>
              )}
            </>
          )}
        </StyledContainer>
        {!isMobile && video && video.href && (
          <StyledPlayPauseButton
            onClick={handlePlayClick}
            paused={paused}
            className={btnPlayPauseClass}
          />
        )}
      </StyledSection>
      {!isMobile &&
        video &&
        videoTranscriptFile &&
        videoTranscriptFile.link &&
        videoTranscriptFile.link.href && (
          <div className={videoTranscriptBackgroundColor}>
            <span className='richtext text-left text-md leading-7 font-graphie text-mattamy-blue mt-2'>
              {'Transcript: '}
              <a
                href={videoTranscriptFileUrl(videoTranscriptFile)}
                download={
                  videoTranscriptFile.name +
                  '.' +
                  videoTranscriptFileExtension(videoTranscriptFile.extension)
                }
                className='underline'
                aria-label={'Download ' + videoTrancriptText + ' transcript'}
              >
                {videoTrancriptText}
              </a>
            </span>
          </div>
        )}
    </>
  );
};

export default ProductHero;
